
import React from 'react';

export enum AIProvider {
  GEMINI = 'gemini',
  OPENROUTER = 'openrouter',
  OLLAMA = 'ollama',
  LMSTUDIO = 'lmstudio',
  LOCAL = 'local'
}

export enum TTSProvider {
  GEMINI = 'gemini',
  ELEVENLABS = 'elevenlabs',
  OPENAI = 'openai'
}

export enum LiveVoiceProvider {
  GEMINI = 'gemini',
  ELEVENLABS = 'elevenlabs',
  OPENAI = 'openai',
  ELEVENLABS_REALTIME = 'elevenlabs-realtime'
}

export enum CostMode {
  FREE_FIRST = 'free-first',
  CHEAP_PAID_FALLBACK = 'cheap-paid-fallback',
  BEST_QUALITY = 'best-quality'
}

export enum SearchRegion {
  NL = 'nl',
  BENELUX = 'benelux',
  EUROPE = 'europe'
}

export enum GeminiModel {
  FLASH_3 = "gemini-2.5-flash",
  PRO_3 = "gemini-2.5-pro",
  LITE_3 = "gemini-2.5-flash-lite",
  THINKING_3 = "gemini-2.5-flash-preview-09-2025",
  LIVE = "gemini-2.5-flash-native-audio-preview-12-2025",
  // Nieuwste generatie (2026) — standaard voor chat en research.
  FLASH_4 = "gemini-3.8-flash",
  PRO_4 = "gemini-3.1-pro-preview",
  LITE_4 = "gemini-3.1-flash-lite",
  ARGON = "gemini-4-argon",
  // Legacy values kept for migration of older saved settings.
  FLASH_1_5 = "gemini-1.5-flash",
  PRO_1_5 = "gemini-1.5-pro"
}

export interface AppSettings {
  geminiKey: string;
  openRouterKey: string;
  openAiKey?: string;
  localApiKey?: string;
  provider: AIProvider;
  costMode: CostMode;
  searchRegion: SearchRegion;
  ttsProvider: TTSProvider;
  liveVoiceProvider?: LiveVoiceProvider;
  localUrl: string; // Voor Ollama/LM Studio
  systemPrompt: string;
  appRules: string;
  globalRules: string;

  // Model Selectie
  defaultModel: string;
  researchModel: string;
  voiceModel: string;
  thinkingBudget: number; // 0 = uit, max 32768
  openAiRealtimeModel?: string;
  openAiRealtimeVoice?: string;
  openAiTranscriptionModel?: string;
  audioInputDeviceId?: string;
  elevenLabsAgentId?: string;

  // Spraak instellingen
  autoSpeak: boolean;
  voiceName: string;
  language: 'nl' | 'en';
  voiceMood: 'Neutral' | 'Cheerful' | 'Professional' | 'CasualProfessional' | 'Calm' | 'Excited' | 'Serious';
  voiceSpeed: 'Slow' | 'Normal' | 'Fast';
  theme: 'light' | 'dark';
  customPresets: PromptPreset[];
  uiTheme: UIThemeColors;
  uiThemePresets: UIThemePreset[];
  
  // Test Mode Settings
  appMode: 'test' | 'live';
  testEmailAddress: string;
  disableCallAgent?: boolean;
}

export interface UIThemeColors {
  canvas: string;
  canvasAccent: string;
  surface: string;
  surfaceAlt: string;
  sidebar: string;
  input: string;
  border: string;
  text: string;
  mutedText: string;
  accent: string;
  accentText: string;
}

export interface UIThemePreset {
  id: string;
  name: string;
  colors: UIThemeColors;
}

export interface PromptPreset {
  id: string;
  name: string;
  systemPrompt: string;
  appRules: string;
  isInternal?: boolean;
}

export interface SelectedSkill {
  id: string;
  name: string;
  category: string;
  path: string;
  description?: string;
}

export interface Company {
  id: string;
  name: string;
  industry: string;
  description: string;
  website: string;
  location: string;
  contactName?: string;
  email?: string;
  phone?: string;
}

export interface SavedSearch {
  id: string;
  name: string;
  context: string;
  timestamp: string;
}

export interface SavedPrompt {
  id: string;
  title: string;
  content: string;
  category: 'General' | 'Lead Finder' | 'Lead Scorer' | 'Research';
}

export interface FileAttachment {
  id: string;
  name: string;
  type: string;
  size: number;
  data: string;
  textContent?: string;
}

export interface Project {
  id: string;
  name: string;
  description: string;
  senderEmail?: string;
  senderContactBlock?: string;
  selectedSkills?: SelectedSkill[];
  createdAt: string;
  customData?: Company[];
  savedSearches?: SavedSearch[];
  clientPhoto?: FileAttachment;
  attachments?: FileAttachment[];
  settings?: AppSettings;
  autopilot?: ProjectAutopilotSettings;
}

export interface AgentResult {
  id: string;
  agentId: string;
  projectId: string;
  timestamp: string;
  summary: string;
  data: any;
  isDeepResearch?: boolean;
}

export interface Agent {
  id: string;
  name: string;
  type: 'lead-finder' | 'lead-scorer' | 'researcher' | 'email-outreach' | 'ai-caller' | 'strategy-agent';
  description: string;
  icon: React.ReactNode;
}

export interface Message {
  id: string;
  role: 'user' | 'model';
  content: string;
  timestamp: number | string;
  type?: 'text' | 'audio' | 'transcription';
  sources?: Array<{ title: string; uri: string }>;
}

export interface ChatSession {
  id: string;
  projectId: string | null; // null = global/lobby
  name: string;
  messages: Message[];
  createdAt: number | string;
  updatedAt?: string;
}

export type InteractionType = 'email' | 'call' | 'whatsapp' | 'meeting' | 'system_log';

export interface Interaction {
  id: string;
  type: InteractionType;
  direction: 'inbound' | 'outbound';
  timestamp: string;
  summary: string;
  sentiment: 'positive' | 'negative' | 'neutral';
  details?: any;
}

export interface CompanyMemory {
  companyId: string;
  projectId: string;
  history: Interaction[];
  patterns: {
    successfulAngles: string[];
    failedAngles: string[];
    commonObjections: string[];
    preferredTone: string;
  };
  updatedAt: string;
}

// NIEUW: Global Memory voor algemene training
export interface GlobalMemory {
  trainingNotes: string[]; // Lijst van geleerde lessen van de gebruiker
  lastUpdated: string;
}

export interface Task {
  id: string;
  projectId: string;
  companyId: string;
  title: string;
  dueDate: string;
  type: 'follow-up' | 'call' | 'research' | 'email';
  status: 'pending' | 'completed';
}

export interface EmailDraft {
  to: string;
  from?: string;
  subject: string;
  body: string;
  contactBlock?: string;
  meta: {
    language: string;
    tone: string;
    purpose: string;
  };
}

export interface CallTranscriptPart {
  speaker: 'agent' | 'contact';
  text: string;
}

export interface CallSessionResult {
  mode: 'test' | 'live' | 'live-voice';
  transcript: CallTranscriptPart[];
  rawTranscript?: string;
  summary: string;
  sentiment: 'positive' | 'negative' | 'neutral';
  objections: string[];
  nextSteps: string[];
  score: number;
}

export interface CallAnalysisResult extends CallSessionResult {
  outcome: string;
  successfulAngles: string[];
  failedAngles: string[];
  commonObjections: string[];
  coachingNotes: string[];
  factualNotes: string[];
}

export interface AutopilotRule {
  id: string;
  sourceAgentId: string;
  targetAgentId: string;
  condition: 'always' | 'has_website' | 'high_score';
  enabled: boolean;
}

export interface ProjectAutopilotSettings {
  isEnabled: boolean;
  rules: AutopilotRule[];
}

export interface FullDossier {
  version: string;
  company: Company;
  memory: CompanyMemory;
  researchResults: AgentResult[];
  tasks: Task[];
  exportedAt: string;
}
